import { useEffect, useState, useMemo } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, BarChart3, User, LogOut, Loader2, AlertTriangle, RefreshCw, LifeBuoy,
  Briefcase, MessageCircle,
} from "lucide-react";
import { api, logout } from "../lib/api";
import { LogoMark } from "../components/Logo";
import { VendorOnboarding } from "./VendorOnboarding";
import DashboardHeader from "../components/DashboardHeader";
import RestoreBanner from "../components/RestoreBanner";
import IdVerificationBanner from "../components/IdVerificationBanner";
import { useDashboardI18n } from "../lib/useDashboardI18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

const BASE_NAV = [
  { to: "/vendor",            icon: LayoutDashboard, key: "tab_overview",  end: true },
  { to: "/vendor/guest-book", icon: Users,           key: "tab_customers" },
  { to: "/vendor/analytics",  icon: BarChart3,       key: "tab_analytics" },
  { to: "/vendor/profile",    icon: User,            key: "tab_profile" },
];

const DRIVER_NAV_ITEM   = { to: "/vendor/jobs",     icon: Briefcase,     key: "tab_jobs" };
const NONDRIVER_NAV_ITEM = { to: "/vendor/messages", icon: MessageCircle, key: "tab_messages" };

export default function VendorLayout() {
  const { t } = useDashboardI18n("vendor");
  const [me, setMe] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const navigate = useNavigate();

  const [reapply, setReapply] = useState(null); // {code, message_en/es, reapply_path}

  const load = async () => {
    setError(null);
    setReapply(null);
    setLoading(true);
    try {
      const meRes = await api.get("/auth/me");
      if (!meRes.data) { navigate("/", { replace: true }); return; }
      setMe(meRes.data);
      if (meRes.data.role !== "vendor" && meRes.data.role !== "admin") {
        setError("This dashboard is for vendor accounts. Contact an administrator to upgrade.");
        return;
      }
      const vRes = await api.get("/vendor/me");
      setVendor(vRes.data);
      if (!vRes.data.onboarding_dismissed) setShowOnboarding(true);
    } catch (e) {
      if (e.response?.status === 404) {
        // iter200 — Structured detail with a reapply_path means the
        // vendor doc was deleted (post-wipe). Show a friendly reapply
        // UI instead of a dead-end.
        const d = e.response?.data?.detail;
        if (d && typeof d === "object" && d.code === "vendor_not_found") {
          setReapply(d);
        } else {
          setError("We could not find your vendor profile. This may be a temporary issue.");
        }
      } else if (e.response?.status === 401) { navigate("/", { replace: true }); return; }
      else setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // iter212 — Insert Jobs (driver) or Messages (non-driver) tab in slot 2.
  // Declared BEFORE conditional early returns so React hook order stays stable.
  const NAV = useMemo(() => {
    if (!vendor) return BASE_NAV;
    const insertAt = 1;   // After Overview
    // iter215 — Tab logic keyed off `category_slug` (Level-1 category)
    // instead of the legacy `service_type` field.
    const isTransport = vendor.category_slug === "transportation"
                        || vendor.service_type === "driver";
    const extra = isTransport ? DRIVER_NAV_ITEM : NONDRIVER_NAV_ITEM;
    return [...BASE_NAV.slice(0, insertAt), extra, ...BASE_NAV.slice(insertAt)];
  }, [vendor]);
  const gridCols = NAV.length === 5 ? "grid-cols-5" : "grid-cols-4";

  const retry = async () => {
    setRetrying(true);
    try { await load(); } finally { setRetrying(false); }
  };

  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };

  if (loading) return <div className="min-h-screen grid place-items-center" data-testid="vendor-loading"><Loader2 className="w-6 h-6 animate-spin text-gold" strokeWidth={1.5}/></div>;

  // iter200 — Friendly reapply flow shown when the vendor doc was
  // removed but the user still has role=vendor. Always give the user
  // an action instead of a dead-end error.
  if (reapply) {
    const lang = (me?.language === "es") ? "es" : "en";
    const message = lang === "es" ? reapply.message_es : reapply.message_en;
    const heading = lang === "es" ? "Perfil de vendedor no encontrado" : "Vendor profile not found";
    const cta = lang === "es" ? "Volver a aplicar" : "Reapply";
    const back = lang === "es" ? "Volver al inicio" : "Back to app";
    const signout = lang === "es" ? "Cerrar sesión" : "Sign out";
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="vendor-reapply">
        <div className="lux-card max-w-md p-8 text-center" style={{ background: "#171717" }}>
          <span className="mx-auto w-12 h-12 grid place-items-center rounded-full border border-gold-soft text-gold">
            <AlertTriangle className="w-5 h-5" strokeWidth={1.5}/>
          </span>
          <div className="font-display mt-4 text-foreground" style={{ fontSize: 24 }}>{heading}</div>
          <div className="text-foreground/70 mt-3" style={{ fontSize: 16, lineHeight: 1.5 }}>{message}</div>
          <div className="mt-7 flex flex-col items-stretch gap-3">
            <Link
              to={reapply.reapply_path || "/get-listed/vendor"}
              data-testid="vendor-reapply-btn"
              className="btn-gold py-3 px-5 inline-flex items-center justify-center gap-2"
              style={{ minHeight: 48, fontSize: 15 }}
            >
              {cta}
            </Link>
            <div className="flex items-center justify-center gap-4 text-[12px] text-foreground/50 pt-2 border-t border-gold-soft/20">
              <Link to="/" className="hover:text-foreground" data-testid="vendor-reapply-back">{back}</Link>
              <span>·</span>
              <button onClick={doLogout} className="hover:text-foreground" data-testid="vendor-reapply-logout">{signout}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="vendor-error">
        <div className="lux-card max-w-md p-8 text-center" style={{ background: "#171717" }}>
          <span className="mx-auto w-12 h-12 grid place-items-center rounded-full border border-gold-soft text-amber-300">
            <AlertTriangle className="w-5 h-5" strokeWidth={1.5}/>
          </span>
          <div className="font-display text-2xl mt-4 text-foreground">Vendor dashboard unavailable</div>
          <div className="text-sm text-foreground/65 mt-2">{error}</div>
          <div className="mt-6 flex flex-col items-stretch gap-3">
            <button
              onClick={retry}
              disabled={retrying}
              data-testid="vendor-retry"
              className="btn-gold py-3 px-5 text-xs inline-flex items-center justify-center gap-2"
              style={{ minHeight: 48 }}
            >
              {retrying ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5}/> : <RefreshCw className="w-4 h-4" strokeWidth={1.5}/>}
              Try again
            </button>
            <Link
              to="/support/tickets"
              data-testid="vendor-contact-support"
              className="text-xs text-foreground/65 hover:text-gold inline-flex items-center justify-center gap-1.5"
              style={{ minHeight: 44 }}
            >
              <LifeBuoy className="w-3.5 h-3.5" strokeWidth={1.5}/> Contact support
            </Link>
            <div className="flex items-center justify-center gap-4 text-[11px] text-foreground/45 pt-2 border-t border-gold-soft/20">
              <Link to="/" className="hover:text-foreground" data-testid="vendor-back-home">Back to app</Link>
              <span>·</span>
              <button onClick={doLogout} className="hover:text-foreground" data-testid="vendor-logout">{t("nav_signout")}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const reactivation = vendor?.reactivation;

  return (
    <div className="min-h-screen flex bg-background text-foreground" data-testid="vendor-dashboard-shell">
      {showOnboarding && (
        <VendorOnboarding
          onClose={async (dismissPermanently) => {
            setShowOnboarding(false);
            if (dismissPermanently) {
              try {
                await api.patch("/vendor/me", { onboarding_dismissed: true });
                setVendor((v) => ({ ...v, onboarding_dismissed: true }));
              } catch { /* ignore */ }
            }
          }}
        />
      )}

      <aside className="hidden md:flex md:flex-col w-64 shrink-0 border-r border-gold-soft p-5">
        <Link to="/vendor" className="flex items-center gap-3 mb-6">
          <LogoMark iconOnly />
          <div>
            <div className="font-display text-lg text-foreground leading-none">{vendor?.full_name}</div>
            <div className="eyebrow mt-1">Vendor · §3</div>
          </div>
        </Link>

        {reactivation && (
          <div className={`lux-card p-3 mb-4 ${reactivation.active ? "border-gold-strong" : "border-destructive/50"}`} data-testid="reactivation-badge-sidebar">
            <div className="eyebrow">{reactivation.active ? "Active" : "Inactive"}</div>
            <div className={`font-display text-xl mt-1 ${reactivation.active ? "text-gold" : "text-destructive"}`}>
              {reactivation.active ? `${reactivation.days_remaining}d` : "Reactivate"}
            </div>
            <div className="text-[10px] text-foreground/55 mt-1">
              {reactivation.active ? "until you need to reactivate" : "hidden from listings"}
            </div>
          </div>
        )}

        <nav className="space-y-1 flex-1" data-testid="vendor-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.key}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-gold/10 text-gold border border-gold-soft" : "text-foreground/70 hover:text-foreground border border-transparent"
                }`}>
                <Icon className="w-4 h-4" strokeWidth={1.5}/>
                <span>{t(n.key)}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-6 lux-card p-3">
          <div className="eyebrow">Signed in as</div>
          <div className="text-xs text-foreground/65 truncate mt-1">{me?.email}</div>
          <button onClick={doLogout} className="mt-3 btn-outline py-1.5 px-3 text-[11px] w-full" data-testid="vendor-logout">
            <LogOut className="w-3 h-3" strokeWidth={1.5}/> {t("nav_signout")}
          </button>
        </div>
      </aside>

      {/* Mobile header — shared DashboardHeader component */}
      <DashboardHeader
        kind="vendor"
        ownerName={vendor?.full_name}
        ownerAvatarUrl={
          (vendor?.selfie_photo?.thumbnail || vendor?.selfie_photo?.medium)
            ? `${process.env.REACT_APP_BACKEND_URL}${vendor.selfie_photo.thumbnail || vendor.selfie_photo.medium}`
            : null
        }
        overviewPath="/vendor"
        profilePath="/vendor/profile"
        onOpenQr={async () => {
          if (!qrData) {
            try { const { data } = await api.get("/vendor/me/qr"); setQrData(data); }
            catch { toast.error("Couldn't load QR"); return; }
          }
          setQrOpen(true);
        }}
      />

      {/* Mobile reactivation pill — sits directly below the header per spec */}
      {reactivation && reactivation.active === false && (
        <div className="md:hidden fixed left-0 right-0 z-30 px-4 pt-2 pb-2"
             style={{ top: 65, background: "#0d0d0d" }}>
          <button
            type="button"
            data-testid="vendor-header-reactivate"
            onClick={async () => {
              try { await api.post("/vendor/me/reactivate"); await load(); toast.success("Profile reactivated"); }
              catch (e) { toast.error(e.response?.data?.detail || e.message); }
            }}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full"
            style={{ background: "#E8B84B", color: "#0B0B0B", minHeight: 48,
                     fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600 }}
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5}/> {t("reactivate_profile")}
          </button>
        </div>
      )}

      {/* QR modal — opened from the header dropdown */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent data-testid="vendor-qr-modal" style={{ background: "#171717" }}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Your Vendor QR</DialogTitle>
            <DialogDescription>Customers scan this to open your profile.</DialogDescription>
          </DialogHeader>
          {qrData && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="p-4 bg-white rounded-md">
                <QRCodeSVG value={qrData.qr_data} size={220} level="M" />
              </div>
              <div className="text-xs font-mono text-foreground/55 break-all text-center max-w-xs">{qrData.qr_data}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <main className="flex-1 min-w-0 md:p-8 px-4 py-6 pt-20 md:pt-8 pb-28 md:pb-8" style={{ background: "#0B0B0B" }}>
        <RestoreBanner kind="vendor" />
        <IdVerificationBanner vendor={vendor} onDone={load} />
        <Outlet context={{ vendor, setVendor, me, reload: load }} />

        {/* Mobile bottom nav per spec */}
        <nav
          className={`md:hidden fixed bottom-0 inset-x-0 z-40 grid ${gridCols}`}
          style={{ background: "#0d0d0d", borderTop: "0.5px solid #1a1a1a", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          data-testid="vendor-bottom-nav"
        >
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end}
                data-testid={`mobile-nav-${n.key}`}
                style={({ isActive }) => ({
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                  paddingTop: 10, paddingBottom: 8,
                  color: isActive ? "#E8B84B" : "#9A9490",
                  borderTop: isActive ? "1.5px solid #E8B84B" : "1.5px solid transparent",
                  fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                  fontFamily: "'DM Sans', sans-serif", minHeight: 48,
                })}>
                <Icon className="w-5 h-5" strokeWidth={1.5}/>
                <span>{t(n.key)}</span>
              </NavLink>
            );
          })}
        </nav>
      </main>
    </div>
  );
}
